import React, { Component } from 'react';
import { SealCheck, Star, LinkedinLogo, FacebookLogo, Cigarette, CigaretteSlash, PawPrint, User, UsersThree, EnvelopeSimple, IdentificationCard, Coins, Wallet, MapPin, Flag, ArrowRight, Clock, WarningCircle, XCircle } from '@phosphor-icons/react';
import { cn, formatBudget, isFullyVerified, toVerificationState, type VerificationState } from '../../lib/utils';
export interface IdentityCardData {
  firstName: string;
  lastName: string;
  avatar: string | null;
  occupation: string;
  bio: string | null;
  memberSinceYear: number;
  rating: number;
  reviewCount: number;
  verifications: {
    email: boolean | VerificationState;
    identity: boolean | VerificationState;
    income: boolean | VerificationState;
  };
  isSmoker: boolean;
  hasPets: boolean;
  pets: string | null;
  livingMode: string;
  linkedinConnected: boolean;
  linkedinUrl: string | null;
  facebookConnected: boolean;
  facebookUrl: string | null;
  activeAnunt: {
    hasActive: boolean;
    budgetMaxEur?: number;
    neighborhoods?: string;
    moveInDate?: string;
  };
}
export interface IdentityCardProps {
  data: IdentityCardData;
  mode: 'owner' | 'public';
  onOpenEditProfile?: (section?: 'social') => void;
  onOpenVerification?: (type: 'email' | 'identity' | 'income') => void;
  onNavigateToAnunt?: () => void;
  incomePending?: boolean;
  isProfileInactive?: boolean;
}
export function IdentityCard({
  data,
  mode,
  onOpenEditProfile,
  onOpenVerification,
  onNavigateToAnunt,
  incomePending = false,
  isProfileInactive = false
}: IdentityCardProps) {
  const fullyVerified = isFullyVerified(data.verifications);
  const hasRating = data.reviewCount > 0 && data.rating > 0;
  return <article className="rounded-2xl border border-dark-4 overflow-hidden bg-dark-2 p-6 sm:p-8" data-id="element-246">
      {/* Top header row */}
      <div className="relative flex flex-col sm:flex-row gap-6 items-start mb-6" data-id="element-247">
        <div className="shrink-0" data-id="element-248">
          {data.avatar ? <img src={data.avatar} alt={`${data.firstName} ${data.lastName}`} className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-dark-1" data-id="element-249" /> : <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-dark-3 flex items-center justify-center text-2xl font-medium text-light-2 border-4 border-dark-1" data-id="element-250">
              {data.firstName[0]}
            </div>}
        </div>

        <div className="flex-1 min-w-0 pt-1" data-id="element-251">
          <div className="flex items-center gap-2 mb-1" data-id="element-252">
            <h1 className="text-2xl sm:text-3xl font-semibold text-light-1 truncate" data-id="element-253">
              {data.firstName} {data.lastName}
            </h1>
            {fullyVerified && <SealCheck size={24} weight="fill" className="text-lime-1 shrink-0" aria-label="Profil verificat complet" data-id="element-254" />}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm sm:text-base font-normal text-light-3" data-id="element-255">
            <span data-id="element-256">{data.occupation}</span>
            {hasRating ? <>
                <span className="text-light-4" data-id="element-257">·</span>
                <div className="flex items-center gap-1" data-id="element-258">
                  <Star size={14} weight="fill" data-id="element-259" />
                  <span data-id="element-260">{data.rating.toFixed(1)}</span>
                </div>
              </> : <>
                <span className="text-light-4" data-id="element-261">·</span>
                <span data-id="element-262">Pe Izio din {data.memberSinceYear}</span>
              </>}
          </div>
          {hasRating && <p className="text-xs text-light-3 mt-1" data-id="element-263">
              Pe Izio din {data.memberSinceYear}
            </p>}
        </div>

        {/* Social icons (top-right) */}
        <div className="absolute top-0 right-0 flex items-center gap-3" data-id="element-264">
          {mode === 'owner' ? <>
              {/* Owner mode: always show both icons, clickable */}
              <button type="button" onClick={() => {
            if (data.linkedinConnected && data.linkedinUrl) {
              window.open(data.linkedinUrl, '_blank', 'noopener,noreferrer');
            } else {
              onOpenEditProfile?.('social');
            }
          }} className="group relative cursor-pointer" aria-label={data.linkedinConnected ? 'Deschide LinkedIn' : 'Conectează LinkedIn'} data-id="element-265">
                <LinkedinLogo size={24} weight="fill" className={cn('transition-colors', data.linkedinConnected ? 'text-light-2 group-hover:text-light-1' : 'text-light-4 group-hover:text-light-2')} data-id="element-266" />
                <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-267">
                  {data.linkedinConnected ? 'LinkedIn conectat' : 'Conectează LinkedIn'}
                </span>
              </button>
              <button type="button" onClick={() => {
            if (data.facebookConnected && data.facebookUrl) {
              window.open(data.facebookUrl, '_blank', 'noopener,noreferrer');
            } else {
              onOpenEditProfile?.('social');
            }
          }} className="group relative cursor-pointer" aria-label={data.facebookConnected ? 'Deschide Facebook' : 'Conectează Facebook'} data-id="element-268">
                <FacebookLogo size={24} weight="fill" className={cn('transition-colors', data.facebookConnected ? 'text-light-2 group-hover:text-light-1' : 'text-light-4 group-hover:text-light-2')} data-id="element-269" />
                <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-270">
                  {data.facebookConnected ? 'Facebook conectat' : 'Conectează Facebook'}
                </span>
              </button>
            </> : <>
              {/* Public mode: only show if connected */}
              {data.linkedinConnected && data.linkedinUrl && <a href={data.linkedinUrl} target="_blank" rel="noopener noreferrer" className="group relative cursor-pointer" aria-label="LinkedIn conectat" data-id="element-271">
                  <LinkedinLogo size={24} weight="fill" className="text-light-3 hover:text-[#0A66C2] transition-colors" data-id="element-272" />
                  <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-273">
                    LinkedIn conectat
                  </span>
                </a>}
              {data.facebookConnected && data.facebookUrl && <a href={data.facebookUrl} target="_blank" rel="noopener noreferrer" className="group relative cursor-pointer" aria-label="Facebook conectat" data-id="element-274">
                  <FacebookLogo size={24} weight="fill" className="text-light-3 hover:text-[#1877F2] transition-colors" data-id="element-275" />
                  <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-276">
                    Facebook conectat
                  </span>
                </a>}
            </>}
        </div>
      </div>

      {/* Trust badges row */}
      <div id={mode === 'owner' ? 'profile-verifications' : undefined} className={cn('flex flex-wrap gap-3', mode === 'owner' && 'scroll-mt-24')} data-id="element-277">
        <TrustBadge Icon={EnvelopeSimple} state={toVerificationState(data.verifications.email)} label="Email" mode={mode} onClick={() => onOpenVerification?.('email')} data-id="element-278" />
        <TrustBadge Icon={IdentificationCard} state={toVerificationState(data.verifications.identity)} label="ID" mode={mode} onClick={() => onOpenVerification?.('identity')} data-id="element-279" />
        <TrustBadge Icon={Coins} state={toVerificationState(data.verifications.income)} label="Venit" mode={mode} onClick={() => onOpenVerification?.('income')} data-id="element-280" />
      </div>

      <div className="border-t border-dark-4 my-6" data-id="element-281"></div>

      {/* Despre mine */}
      {data.bio ? <div data-id="element-282">
          <h2 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-283">
            Despre mine
          </h2>
          <p className="text-sm font-normal text-light-2 leading-relaxed" data-id="element-284">
            {data.bio}
          </p>
        </div> : mode === 'owner' ? <button onClick={() => onOpenEditProfile?.()} className="w-full text-left p-3 rounded-xl border border-dashed border-dark-4 text-sm text-light-3 hover:text-light-1 hover:border-light-4 transition-colors cursor-pointer" data-id="element-285">
          + Adaugă o descriere despre tine
        </button> : null}

      {/* Lifestyle pills */}
      <div className="mt-4 flex flex-wrap gap-3 text-sm text-light-2 bg-dark-1/50 p-3 rounded-xl border border-dark-3/50 inline-flex" data-id="element-286">
        <div className="flex items-center gap-1.5" data-id="element-287">
          {data.isSmoker ? <Cigarette size={16} data-id="element-288" /> : <CigaretteSlash size={16} data-id="element-289" />}
          <span data-id="element-290">{data.isSmoker ? 'Fumător(i)' : 'Nefumător(i)'}</span>
        </div>
        <span className="text-dark-4" data-id="element-291">|</span>
        <div className="flex items-center gap-1.5" data-id="element-292">
          <PawPrint size={16} data-id="element-293" />
          <span data-id="element-294">{data.pets ? data.pets : 'Fără animale'}</span>
        </div>
        <span className="text-dark-4" data-id="element-295">|</span>
        <div className="flex items-center gap-1.5" data-id="element-296">
          {data.livingMode === 'Singur(ă)' ? <User size={16} data-id="element-297" /> : <UsersThree size={16} data-id="element-298" />}
          <span data-id="element-299">{data.livingMode}</span>
        </div>
      </div>

      {/* Caută chirie acum mini-strip */}
      <div className="mt-6 pt-6 border-t border-dark-4/50" data-id="element-300">
        {data.activeAnunt.hasActive && !isProfileInactive ? <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-301">
            <div data-id="element-302">
              <h3 className="text-xs font-medium text-lime-1 uppercase tracking-wider mb-2" data-id="element-303">
                Caută chirie acum
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-light-2" data-id="element-304">
                <div className="flex items-center gap-1.5" data-id="element-305">
                  <Wallet size={16} className="text-light-3" data-id="element-306" />
                  <span data-id="element-307">
                    {formatBudget(data.activeAnunt.budgetMaxEur || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-308">
                  <MapPin size={16} className="text-light-3" data-id="element-309" />
                  <span data-id="element-310">{data.activeAnunt.neighborhoods}</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-311">
                  <Flag size={16} className="text-light-3" data-id="element-312" />
                  <span data-id="element-313">{data.activeAnunt.moveInDate}</span>
                </div>
              </div>
            </div>
            <button onClick={onNavigateToAnunt} className="shrink-0 bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold h-10 px-4 rounded-md text-sm transition-colors flex items-center justify-center gap-2" data-id="element-314">
              {mode === 'owner' ? 'Gestionează anunțul' : 'Vezi anunțul'}{' '}
              <ArrowRight size={16} weight="bold" data-id="element-315" />
            </button>
          </div> : <p className="text-sm text-light-3 italic" data-id="element-316">
            {mode === 'owner' ? 'Nu ai un anunț activ momentan.' : 'Nu caută chirie momentan.'}
          </p>}
      </div>
    </article>;
}
function SocialIconLink({
  Icon,
  url,
  ariaLabel
}: {
  Icon: ComponentType<any>;
  url: string;
  ariaLabel: string;
}) {
  return <a href={url} target="_blank" rel="noopener noreferrer" className="group relative cursor-pointer" aria-label={ariaLabel} data-id="element-317">
      <Icon size={24} weight="fill" className="text-light-3 hover:text-light-1 transition-colors" data-id="element-318" />
      <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-dark-3 text-xs text-light-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity" data-id="element-319">
        {ariaLabel}
      </span>
    </a>;
}
function TrustBadge({
  Icon,
  state,
  label,
  mode,
  onClick
}: {
  Icon: ComponentType<any>;
  state: VerificationState;
  label: string;
  mode: 'owner' | 'public';
  onClick: () => void;
}) {
  if (state.status === 'verified') {
    const displayLabel = state.verifiedAt ? `${label} verificat · ${state.verifiedAt}` : `${label} verificat`;
    return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-lime-5 border-lime-4 text-light-1 text-sm" title={displayLabel} data-id="element-320">
        <Icon size={18} className="text-lime-1" data-id="element-321" />
        <span className="hidden sm:inline" data-id="element-322">{displayLabel}</span>
      </div>;
  }
  if (state.status === 'pending') {
    return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-amber-500/10 border-amber-500/30 text-amber-300 text-sm" title={`${label} în review`} data-id="element-323">
        <Clock size={18} className="text-amber-400" data-id="element-324" />
        <span className="hidden sm:inline" data-id="element-325">{label} · în review</span>
      </div>;
  }
  if (state.status === 'rejected') {
    return mode === 'owner' ? <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 text-sm transition-colors cursor-pointer" title={`${label} respins. Vezi motivul.`} data-id="element-326">
        <WarningCircle size={18} className="text-red-400" data-id="element-327" />
        <span className="hidden sm:inline" data-id="element-328">{label} · respins</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-329">Respins</span>
      </button> : <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-red-500/10 border-red-500/30 text-red-400 text-sm" title={`${label} respins`} data-id="element-330">
        <WarningCircle size={18} className="text-red-400" data-id="element-331" />
        <span className="hidden sm:inline" data-id="element-332">{label} · respins</span>
      </div>;
  }
  if (state.status === 'expired') {
    return mode === 'owner' ? <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20 text-sm transition-colors cursor-pointer" title={`${label} expirat. Re-verifică.`} data-id="element-333">
        <XCircle size={18} className="text-orange-400" data-id="element-334" />
        <span className="hidden sm:inline" data-id="element-335">{label} · expirat</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-336">Expirat</span>
      </button> : <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-orange-500/10 border-orange-500/30 text-orange-400 text-sm" title={`${label} expirat`} data-id="element-337">
        <XCircle size={18} className="text-orange-400" data-id="element-338" />
        <span className="hidden sm:inline" data-id="element-339">{label} · expirat</span>
      </div>;
  }
  if (mode === 'owner') {
    return <button type="button" onClick={onClick} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-dark-3 border-dark-4 text-light-3 hover:text-light-1 hover:border-light-4 text-sm transition-colors cursor-pointer" title={`Verifică ${label.toLowerCase()}`} data-id="element-340">
        <Icon size={18} className="text-light-4" data-id="element-341" />
        <span className="hidden sm:inline" data-id="element-342">Verifică {label.toLowerCase()}</span>
        <span className="sm:hidden text-xs font-medium" data-id="element-343">Verifică</span>
      </button>;
  }
  return <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border bg-dark-3 border-dark-4 text-light-3 text-sm" title={`${label} neverificat`} data-id="element-344">
      <Icon size={18} className="text-light-4" data-id="element-345" />
      <span className="hidden sm:inline" data-id="element-346">{label} neverificat</span>
    </div>;
}